// src/pages/admin/tabs/TransportsTab.jsx
// Independent Transport module: manage the transport registry (name + GST) that
// customers can be assigned to, and which then flows into quotations/slips.
import { B, fonts, Btn, Badge } from "../../../components/UI";

export default function TransportsTab({
  transports,
  filteredTransports,
  paginatedTransports,
  permissions,
  transportSearch,
  setTransportSearch,
  transportPage,
  transportPageSize,
  transportTotalPages,
  setTransportPage,
  showTransportForm,
  setShowTransportForm,
  editTransport,
  setEditTransport,
  viewTransport,
  setViewTransport,
  handleSaveTransport,
  transportSaving,
  handleDeleteTransport,
  TransportForm,
  showToast,
}) {
  if (viewTransport && !editTransport && !showTransportForm) {
    return (
      <div>
        <TransportForm
          initial={viewTransport}
          onDone={() => setViewTransport(null)}
          readOnly
          showToast={showToast}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={() => setViewTransport(null)} variant="ghost">Back</Btn>
          {permissions?.canManageCustomers && (
            <Btn onClick={() => { setEditTransport(viewTransport); setViewTransport(null); setShowTransportForm(false); }} variant="primary">
              Edit Transport
            </Btn>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>{transports.length} total transports</p>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            value={transportSearch}
            onChange={e => setTransportSearch(e.target.value)}
            placeholder="Search transport name or GST..."
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 13px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", width: 240 }}
          />
          {permissions?.canManageCustomers && !showTransportForm && !editTransport && (
            <Btn onClick={() => setShowTransportForm(true)} variant="primary">+ Add Transport</Btn>
          )}
        </div>
      </div>

      {(showTransportForm || editTransport) && (
        <TransportForm
          initial={editTransport || viewTransport}
          onDone={() => { setShowTransportForm(false); setEditTransport(null); setViewTransport(null); }}
          onSubmit={handleSaveTransport}
          saving={transportSaving}
          readOnly={Boolean(viewTransport)}
          showToast={showToast}
        />
      )}

      <div style={{ overflowX: "auto", background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr style={{ background: B.orangeFaint }}>
              {["Transport Name", "GST Number", "Status", "Actions"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 11, color: B.textMid, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "12px 10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedTransports.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "24px", color: B.textLight, fontFamily: fonts.body }}>
                  {transportSearch ? "No transports match your search." : "No transports yet. Add your first transport above."}
                </td>
              </tr>
            ) : (
              paginatedTransports.map(transport => (
                <tr key={transport.id} style={{ borderBottom: `1px solid ${B.borderLight}` }}>
                  <td style={{ padding: "10px", fontFamily: fonts.body, fontWeight: 600 }}>{transport.name || "—"}</td>
                  <td style={{ padding: "10px", fontFamily: fonts.body }}>{transport.gst || "—"}</td>
                  <td style={{ padding: "10px", fontFamily: fonts.body }}>
                    <Badge color={transport.status === "active" ? B.green : B.red}>{transport.status || "active"}</Badge>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button type="button" onClick={() => { setViewTransport(transport); setShowTransportForm(false); setEditTransport(null); }} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }} title="View">👁️</button>
                      <button type="button" onClick={() => { if (!permissions?.canManageCustomers) return; setEditTransport(transport); setShowTransportForm(false); setViewTransport(null); }} disabled={!permissions?.canManageCustomers} style={{ border: "none", background: "transparent", cursor: permissions?.canManageCustomers ? "pointer" : "not-allowed", fontSize: 16, opacity: permissions?.canManageCustomers ? 1 : 0.4 }} title="Edit">✏️</button>
                      <button type="button" onClick={() => permissions?.canManageCustomers && handleDeleteTransport(transport)} disabled={!permissions?.canManageCustomers || transport.status === "deleted"} style={{ border: "none", background: "transparent", cursor: permissions?.canManageCustomers && transport.status !== "deleted" ? "pointer" : "not-allowed", fontSize: 16, opacity: permissions?.canManageCustomers && transport.status !== "deleted" ? 1 : 0.4 }} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredTransports.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
            Showing {(transportPage - 1) * transportPageSize + 1}-{Math.min(transportPage * transportPageSize, filteredTransports.length)} of {filteredTransports.length}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setTransportPage(p => Math.max(1, p - 1))} variant="ghost" small disabled={transportPage === 1}>Previous</Btn>
            <span style={{ fontSize: 12, color: B.textMid, alignSelf: "center", fontFamily: fonts.body }}>Page {transportPage} / {transportTotalPages}</span>
            <Btn onClick={() => setTransportPage(p => Math.min(transportTotalPages, p + 1))} variant="ghost" small disabled={transportPage === transportTotalPages}>Next</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
